import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { useStoredState } from '../hooks/useStoredState'
import { UI_LANGUAGES } from '../utils/constants/language.constants'
import i18n from '../i18n'
import { APP_NAME, IS_PROD } from '../lib/config'

const ENV_SUFFIX = IS_PROD ? '-prod' : '-test'
export const LANGUAGE_KEY = `mezzoforte:${APP_NAME}:language${ENV_SUFFIX}`

interface Language {
  iso: string
  label: string
}

interface LanguageContextType {
  currentLanguage: string
  setLanguage: (lang: string) => void
  availableLanguages: Language[]
}

const LanguageContext = createContext<LanguageContextType>({
  currentLanguage: 'en',
  setLanguage: () => {},
  availableLanguages: UI_LANGUAGES,
})

export const useLanguage = () => useContext(LanguageContext)

interface LanguageContextProviderProps {
  children: React.ReactNode
}

export const LanguageContextProvider: React.FC<LanguageContextProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useStoredState({
    key: LANGUAGE_KEY,
    defaultValue: 'en',
  })

  const setLanguage = (lang: string) => {
    setCurrentLanguage(lang)
    if (i18n && i18n.changeLanguage) {
      i18n.changeLanguage(lang)
    }
  }

  useEffect(() => {
    if (currentLanguage && i18n && i18n.language !== currentLanguage && i18n.changeLanguage) {
      i18n.changeLanguage(currentLanguage)
    }
  }, [currentLanguage])

  const contextValue = useMemo(
    () => ({
      currentLanguage,
      setLanguage,
      availableLanguages: UI_LANGUAGES,
    }),
    [currentLanguage]
  )

  return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>
}
